import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Button } from "@/components/PageUI";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, ClipboardCheck, UserCheck, CalendarRange, Dumbbell, TrendingUp, Copy } from "lucide-react";
import { UpgradeGate } from "@/components/UpgradeGate";

const FEATURES = [
  { key: "practice_planner", label: "Practice Planner", icon: ClipboardCheck, model: "GPT-5.5", mode: "practice" },
  { key: "player_evaluation", label: "Player Evaluation", icon: UserCheck, model: "GPT-5.5", mode: "player" },
  { key: "season_report", label: "Season Report", icon: CalendarRange, model: "Gemini 3.1 Pro", mode: "team" },
  { key: "training_suggestion", label: "Training Suggestions", icon: Dumbbell, model: "Gemini 3.1 Pro", mode: "text", placeholder: "Athlete needs: improve stamina and first touch" },
  { key: "skill_improvement", label: "Skill Improvement Plan", icon: TrendingUp, model: "GPT-5.5", mode: "text", placeholder: "Build a 4-week plan to improve shooting accuracy" },
];

const DURATIONS = ["45", "60", "75", "90", "120"];

export default function AICoach() {
  const { club, isPremium } = useAuth();
  const [usage, setUsage] = useState(null);
  const previewMode = !isPremium && club?.plan === "starter" && !!club?.subscription_active;
  const visibleFeatures = previewMode ? FEATURES.filter((f) => f.key === "practice_planner") : FEATURES;
  const [feature, setFeature] = useState(FEATURES[0]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState("");
  const [teamId, setTeamId] = useState("");
  const [playerId, setPlayerId] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [duration, setDuration] = useState("60");
  const [focus, setFocus] = useState("");

  const loadUsage = () => api.get("/ai/usage").then((r) => setUsage(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data || [])).catch(() => {});
    api.get("/players").then((r) => setPlayers(r.data || [])).catch(() => {});
    loadUsage();
  }, []);

  const selectFeature = (f) => { setFeature(f); setOutput(""); };

  const stream = async (url, payload) => {
    setLoading(true); setOutput("");
    try {
      const res = await fetch(`${API}${url}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(localStorage.getItem("cs_token") ? { Authorization: `Bearer ${localStorage.getItem("cs_token")}` } : {}) },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok || !res.body) { setOutput("Something went wrong. Please try again."); return; }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n"); buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = JSON.parse(line.slice(6));
          if (data.delta) setOutput((o) => o + data.delta);
          if (data.error) toast.error("The AI hit a snag — please try again.");
        }
      }
    } catch { setOutput("Something went wrong generating this. Please try again."); }
    finally { setLoading(false); }
  };

  const run = () => {
    if (previewMode && usage && usage.preview_remaining <= 0) return toast.error("You've used your free Starter practice plan this month. Upgrade to Pro for unlimited AI.");
    if (feature.mode === "practice") { stream("/ai/practice-plan", { team_id: teamId, age_group: ageGroup, duration_min: Number(duration), focus }).then(loadUsage); return; }
    if (feature.mode === "player") { if (!playerId) return toast.error("Pick an athlete"); return stream("/ai/player-evaluation", { player_id: playerId }); }
    if (feature.mode === "team") { if (!teamId) return toast.error("Pick a team"); return stream("/ai/season-report", { team_id: teamId }); }
    if (!context.trim()) return;
    return stream("/ai/generate", { feature: feature.key, sport: club?.sport || "soccer", context });
  };

  if (!isPremium && !previewMode) return (
    <div className="animate-fade-up">
      <PageHeader title="AI Coach" subtitle="Premium AI tools powered by GPT-5.5 and Gemini 3.1 Pro." testid="ai-header" />
      <UpgradeGate feature="AI Coach" desc="Generate practice plans, player evaluations, season reports and training suggestions with AI. Upgrade to Pro or Club to unlock the AI Coach." testid="ai-upgrade-gate" />
    </div>
  );

  return (
    <div className="animate-fade-up">
      <PageHeader title="AI Coach" subtitle="Premium AI tools powered by GPT-5.5 and Gemini 3.1 Pro." testid="ai-header" />
      {previewMode && (
        <div className="mb-4 rounded-xl border border-primary/30 bg-primary/10 p-4 flex items-center justify-between gap-4" data-testid="starter-preview-banner">
          <div className="flex items-center gap-3">
            <Sparkles size={18} className="text-primary" />
            <p className="text-sm"><b>Starter preview:</b> {usage?.preview_remaining ?? 1} free AI practice plan{(usage?.preview_remaining ?? 1) === 1 ? "" : "s"} left this month. <span className="text-muted-foreground">Upgrade to Pro for unlimited AI Coach tools.</span></p>
          </div>
          <Button asChild size="sm" className="brand-gradient text-white border-0 shrink-0" data-testid="preview-upgrade-btn"><a href="/app/payments">Upgrade</a></Button>
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="space-y-2">
          {visibleFeatures.map((f) => (
            <button key={f.key} onClick={() => selectFeature(f)} data-testid={`ai-feature-${f.key}`}
              className={`w-full text-left rounded-xl border p-4 transition-colors duration-150 ${feature.key === f.key ? "border-primary bg-primary/10" : "border-border bg-card hover:bg-accent"}`}>
              <div className="flex items-center gap-3">
                <f.icon size={18} className={feature.key === f.key ? "text-primary" : "text-muted-foreground"} />
                <div>
                  <p className="font-semibold text-sm">{f.label}</p>
                  <p className="text-xs text-muted-foreground">{f.model}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl border border-border bg-card p-6">
            <label className="font-heading font-semibold text-lg flex items-center gap-2"><feature.icon size={18} className="text-primary" /> {feature.label}</label>

            {feature.mode === "practice" && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="ai-practice-form">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Team (optional)</p>
                  <Select value={teamId} onValueChange={setTeamId}>
                    <SelectTrigger data-testid="ai-team-select"><SelectValue placeholder="Any team" /></SelectTrigger>
                    <SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Age group / level</p>
                  <Input value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} placeholder="e.g. U12 / beginners" data-testid="ai-age-input" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Duration</p>
                  <Select value={duration} onValueChange={setDuration}>
                    <SelectTrigger data-testid="ai-duration-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{DURATIONS.map((d) => <SelectItem key={d} value={d}>{d} min</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Skill goals / focus</p>
                  <Input value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="e.g. passing, spacing" data-testid="ai-focus-input" />
                </div>
              </div>
            )}

            {feature.mode === "player" && (
              <div className="mt-4" data-testid="ai-player-form">
                <p className="text-xs text-muted-foreground mb-1">Athlete</p>
                <Select value={playerId} onValueChange={setPlayerId}>
                  <SelectTrigger data-testid="ai-player-select"><SelectValue placeholder="Pick an athlete" /></SelectTrigger>
                  <SelectContent>{players.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">Uses this athlete's real ratings, attendance and match stats.</p>
              </div>
            )}

            {feature.mode === "team" && (
              <div className="mt-4" data-testid="ai-season-form">
                <p className="text-xs text-muted-foreground mb-1">Team</p>
                <Select value={teamId} onValueChange={setTeamId}>
                  <SelectTrigger data-testid="ai-season-team-select"><SelectValue placeholder="Pick a team" /></SelectTrigger>
                  <SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">Pulls the team's real record, roster ratings and attendance.</p>
              </div>
            )}

            {feature.mode === "text" && (
              <Textarea value={context} onChange={(e) => setContext(e.target.value)} placeholder={feature.placeholder} rows={4} className="mt-3" data-testid="ai-context-input" />
            )}

            <Button onClick={run} disabled={loading} className="mt-4 brand-gradient text-white border-0 hover:opacity-90" data-testid="ai-generate-btn">
              <Sparkles size={16} className="mr-1.5" /> {loading ? "Generating..." : "Generate"}
            </Button>
          </div>

          {(output || loading) && (
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="ai-output">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-wider text-secondary font-semibold">AI Output</p>
                {output && !loading && <button onClick={() => { navigator.clipboard.writeText(output); toast.success("Copied"); }} className="text-muted-foreground hover:text-primary" data-testid="ai-copy-btn"><Copy size={15} /></button>}
              </div>
              <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap text-sm leading-relaxed">{output || "Thinking..."}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
